import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod";

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge to handle conflicts
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to locale string
 */
export function formatDate(date: Date | string | null | undefined, locale = "pl-PL"): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Format date and time to locale string
 */
export function formatDateTime(date: Date | string | null | undefined, locale = "pl-PL"): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Extract error message from API error response
 */
export function getApiErrorMessage(err: unknown, fallback = "Wystąpił błąd"): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { error?: string; message?: string; detail?: string } } }).response;
    return response?.data?.error || response?.data?.message || response?.data?.detail || fallback;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

/**
 * Format cents as Polish currency (e.g. "1 000,00 zł", "500 zł")
 */
export function formatCurrency(cents: number, locale = "pl-PL"): string {
  const value = cents / 100;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format decimal hours to "Xh Ymin" format (e.g. 8.5 → "8h 30min")
 */
export function formatHours(decimalHours: number): string {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}min`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}min`;
}

/**
 * Polish month names (nominative case, capitalized)
 */
export const MONTH_NAMES = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień",
] as const;

/**
 * Polish month names (genitive case, lowercase) - for dates like "5 stycznia 2025"
 */
export const MONTH_NAMES_GENITIVE = [
  "stycznia", "lutego", "marca", "kwietnia", "maja", "czerwca",
  "lipca", "sierpnia", "września", "października", "listopada", "grudnia",
] as const;

/**
 * Format minutes to "Xh Ymin" display string
 * e.g. 90 → "1h 30min", 60 → "1h", 45 → "45min"
 */
export function formatDuration(minutes: number, timeUnit?: string): string {
  if (timeUnit === "lesson_count") return `${minutes} zaj.`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0 && mins > 0) return `${hours}h ${mins}min`;
  if (hours > 0) return `${hours}h`;
  return `${mins}min`;
}

/**
 * Format date range to locale string (DD.MM.YYYY - DD.MM.YYYY)
 * If dates are the same, returns single date
 */
export const passwordSchema = z
  .string()
  .min(8, "Hasło musi mieć minimum 8 znaków")
  .regex(/[A-Z]/, "Hasło musi zawierać wielką literę")
  .regex(/[0-9]/, "Hasło musi zawierać cyfrę")
  .regex(/[^A-Za-z0-9]/, "Hasło musi zawierać znak specjalny");

/**
 * Sanitize filename: lowercase, no Polish characters, hyphens instead of spaces.
 * Consistent with backend sanitizeFilename().
 */
export function sanitizeFilename(name: string): string {
  const polishChars: Record<string, string> = {
    'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
    'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
    'Ą': 'a', 'Ć': 'c', 'Ę': 'e', 'Ł': 'l', 'Ń': 'n',
    'Ó': 'o', 'Ś': 's', 'Ź': 'z', 'Ż': 'z',
  };

  return name
    .split('')
    .map(char => polishChars[char] || char)
    .join('')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

export function formatDateRange(
  dateFrom: string | Date,
  dateTo: string | Date,
  locale = "pl-PL"
): string {
  const formatOptions: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  const from = typeof dateFrom === "string" ? new Date(dateFrom) : dateFrom;
  const to = typeof dateTo === "string" ? new Date(dateTo) : dateTo;

  const fromStr = from.toLocaleDateString(locale, formatOptions);
  const toStr = to.toLocaleDateString(locale, formatOptions);

  // If same day, show only one date
  const fromDate = typeof dateFrom === "string" ? dateFrom : dateFrom.toISOString().split("T")[0];
  const toDate = typeof dateTo === "string" ? dateTo : dateTo.toISOString().split("T")[0];

  if (fromDate === toDate) {
    return fromStr;
  }

  return `${fromStr} - ${toStr}`;
}

